import React, { useLayoutEffect, useRef, useState } from 'react';
import { arredondarCasas, formatarNumero, normalizarNumeroColado, numeroDoValor, simboloMoeda } from '../util/mascaraNumero';

/**
 * Campos numéricos pt-BR que "funcionam de verdade" (QA 26/09, rodada 2).
 *
 *   <CampoValor moeda="dolar" value={x} onValueChange={({ value }) => set(value)} />
 *   <CampoPercentual value={p} decimalScale={4} onValueChange={({ value }) => set(value)} />
 *
 * Modelo de digitação (igual para digitar, colar, arrastar e autopreencher):
 *  - FORA do foco o campo mostra o valor formatado: "R$ 1.234,56", "19,05%".
 *  - NO foco vira texto livre, sem prefixo nem milhar ("1234,56"), tudo selecionado.
 *    O usuário digita dígitos, um "-" no começo, "." e "," à vontade; setas,
 *    Home/End e clique posicionam o cursor normalmente. A máscara NUNCA reescreve
 *    o texto debaixo do cursor enquanto o campo está focado.
 *  - A cada tecla o texto é lido com a MESMA regra da colagem
 *    (util/mascaraNumero → normalizarNumeroColado) e o valor vai para o pai na
 *    hora — os totais da grade continuam ao vivo. Texto vazio ou incompleto ("-")
 *    não é enviado enquanto focado (o pai não reinjeta 0/1 debaixo do cursor).
 *  - No blur/Enter o texto é lido de novo, formatado, e o vazio é confirmado.
 *
 * Regras de leitura (valor, com milhar): "1.500,00", "1,500.00", "1.500" → 1500;
 * "12.75" → 12,75; "1500,5" → 1500,5. Vírgula ÚNICA com 3 dígitos ("1,500") é 1,5
 * — decisão pt-BR (vírgula é o decimal). Percentual/câmbio (`milhar={false}`):
 * "." e "," são decimal ("19.05" → 19,05).
 *
 * API compatível com o NumericFormat que substitui: value (number, "1500.5" ou
 * "1.500,50"), onValueChange({ value, floatValue, formattedValue }, { event, source }),
 * decimalScale, fixedDecimalScale, allowNegative (padrão true, como antes),
 * isAllowed, className, style, id, placeholder, readOnly, disabled, aria-*.
 * `casasMinimas`: casas sempre exibidas quando não fixas (padrão: 2 no valor,
 * 0 no percentual) — "R$ 1.234,560000" passa a ser "R$ 1.234,56".
 */
export function CampoNumeroBase({
    milhar = true,
    prefix = '',
    suffix = '',
    decimalScale = 2,
    fixedDecimalScale = true,
    casasMinimas,
    allowNegative = true,
    selecionarAoFocar = true,
    value,
    defaultValue,
    onValueChange,
    isAllowed,
    onChange,
    onPaste,
    onFocus,
    onBlur,
    onKeyDown,
    onMouseDown,
    onMouseUp,
    // props só do NumericFormat (ignoradas)
    type, thousandSeparator, decimalSeparator, allowedDecimalSeparators, valueIsNumericString,
    allowLeadingZeros, thousandsGroupStyle, customInput, renderText, getInputRef, displayType,
    ...props
}) {
    const inputRef = useRef(null);
    const [editando, setEditando] = useState(false);
    const [texto, setTexto] = useState('');
    const alterado = useRef(false);
    const ultimoEmitido = useRef('');
    const focoPeloMouse = useRef(false);
    const selecionarAgora = useRef(false);
    const interagiu = useRef(false); // tecla/clique depois do foco: a seleção adiada não pode mais atropelar

    const casasMax = decimalScale === null || decimalScale === undefined ? null : decimalScale;
    const casasMin = fixedDecimalScale && casasMax !== null
        ? casasMax
        : Math.min(casasMinimas !== undefined ? casasMinimas : 2, casasMax === null ? Infinity : casasMax);
    const formato = { milhar, casasMin, casasMax, prefixo: prefix, sufixo: suffix };
    const numeroAtual = numeroDoValor(value !== undefined ? value : defaultValue);
    const exibido = formatarNumero(numeroAtual, formato);
    const textoDeEdicao = (numero) => formatarNumero(numero, { milhar: false, casasMin, casasMax });
    const bloqueado = !!(props.readOnly || props.disabled);

    // texto livre → string canônica ("1500.5"), '' se não há número
    const interpretar = (bruto) => {
        const numero = normalizarNumeroColado(bruto, { milhar });
        if (numero === null) return '';
        let final = arredondarCasas(numero, casasMax);
        if (!allowNegative) final = final.replace('-', '');
        return final;
    };
    const montarValores = (numero) => {
        if (numero === '') return { value: '', floatValue: undefined, formattedValue: '' };
        const v = fixedDecimalScale && casasMax ? formatarNumero(numero, { milhar: false, casasMin: casasMax, casasMax }).replace(',', '.') : numero;
        const floatValue = parseFloat(v);
        return { value: v, floatValue: Number.isNaN(floatValue) ? undefined : floatValue, formattedValue: formatarNumero(v, formato) };
    };
    const emitir = (numero, evento) => {
        ultimoEmitido.current = numero;
        if (onValueChange) onValueChange(montarValores(numero), { event: evento, source: 'event' });
    };
    const permitido = (numero) => numero === '' || !isAllowed || isAllowed(montarValores(numero));

    const selecionarTudo = () => {
        const el = inputRef.current;
        if (!selecionarAoFocar || !el || bloqueado || document.activeElement !== el) return;
        el.select();
    };
    useLayoutEffect(() => {
        if (editando && selecionarAgora.current) {
            selecionarAgora.current = false;
            selecionarTudo();
            // o mousedown do navegador posiciona o cursor depois do foco. Em tela pesada
            // (grade grande) este timeout pode rodar DEPOIS de uma seta/End já digitada:
            // aí não seleciona de novo (senão a próxima tecla apagaria tudo).
            setTimeout(() => { if (!alterado.current && !interagiu.current) selecionarTudo(); }, 0);
        }
    });

    const tratarFoco = (e) => {
        if (!bloqueado) {
            alterado.current = false;
            interagiu.current = false;
            ultimoEmitido.current = numeroAtual;
            selecionarAgora.current = true;
            setTexto(textoDeEdicao(numeroAtual));
            setEditando(true);
        }
        if (onFocus) onFocus(e);
    };

    const confirmar = (evento) => {
        const final = interpretar(texto);
        if (alterado.current && final !== ultimoEmitido.current && permitido(final)) emitir(final, evento);
        return final;
    };

    const tratarBlur = (e) => {
        if (editando) {
            confirmar(e);
            setEditando(false);
        }
        focoPeloMouse.current = false;
        if (onBlur) onBlur(e);
    };

    const tratarTecla = (e) => {
        interagiu.current = true;
        // Tecla imprimível que deixaria o texto inválido (letra, espaço, "%", "-" fora do lugar):
        // bloqueia no keydown para o navegador não apagar a seleção nem mover o cursor.
        if (editando && e.key && e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
            const el = e.target;
            const inicio = el.selectionStart ?? el.value.length;
            const fim = el.selectionEnd ?? el.value.length;
            const provavel = el.value.slice(0, inicio) + e.key + el.value.slice(fim);
            const digitavel = allowNegative ? /^[-−]?[\d.,]*$/ : /^[\d.,]*$/;
            if (!digitavel.test(provavel)) {
                e.preventDefault();
                if (onKeyDown) onKeyDown(e);
                return;
            }
        }
        // Home/End: no macOS o Chrome rola a página e não move o cursor do input.
        // Aqui valem como no Windows: início/fim do texto (Shift estende a seleção).
        if ((e.key === 'Home' || e.key === 'End') && editando && !e.metaKey && !e.ctrlKey && !e.altKey) {
            const el = e.target;
            const fim = el.value.length;
            if (e.key === 'Home') el.setSelectionRange(0, e.shiftKey ? el.selectionEnd : 0, e.shiftKey ? 'backward' : 'none');
            else el.setSelectionRange(e.shiftKey ? el.selectionStart : fim, fim);
            e.preventDefault();
        }
        if (e.key === 'Enter' && editando) {
            const final = confirmar(e);
            if (alterado.current) setTexto(textoDeEdicao(final === '' ? '' : montarValores(final).value));
        }
        if (onKeyDown) onKeyDown(e);
    };

    const aplicarTexto = (novoTexto, e) => {
        const numero = interpretar(novoTexto);
        if (!permitido(numero)) return false;
        setTexto(novoTexto);
        alterado.current = true;
        if (numero !== '' && numero !== ultimoEmitido.current) emitir(numero, e);
        return true;
    };

    const tratarMudanca = (e) => {
        let novo = e.target.value;
        const digitavel = allowNegative ? /^[-−]?[\d.,]*$/ : /^[\d.,]*$/;
        if (!digitavel.test(novo)) {
            // Tecla inválida (letra, "%", "-" no meio): ignora, sem mexer no cursor.
            // Texto que chega inteiro (autopreenchimento, arrastar e soltar): lê como colagem.
            const tipo = e.nativeEvent && e.nativeEvent.inputType;
            if (tipo === 'insertText' || tipo === 'insertCompositionText') return;
            const numero = interpretar(novo);
            if (numero === '') return;
            novo = textoDeEdicao(numero);
        }
        if (aplicarTexto(novo.replace('−', '-'), e) && onChange) onChange(e);
    };

    const tratarColagem = (e) => {
        if (onPaste) onPaste(e);
        if (e.defaultPrevented || bloqueado) return;
        const bruto = (e.clipboardData || (typeof window !== 'undefined' && window.clipboardData))?.getData('text') ?? '';
        e.preventDefault();
        const numero = interpretar(bruto);
        if (numero === '') return; // nada numérico: ignora
        // A colagem substitui o campo inteiro (o foco já seleciona tudo).
        if (!aplicarTexto(textoDeEdicao(numero), e)) return;
        const el = e.target;
        setTimeout(() => { try { el.setSelectionRange(el.value.length, el.value.length); } catch (erro) { /* sem seleção */ } }, 0);
    };

    // Primeiro clique: o mouseup do Safari/Chrome desfaria a seleção total do foco.
    // Clique com o campo JÁ focado não passa por aqui: posiciona o cursor normalmente.
    const tratarMouseDown = (e) => {
        if (document.activeElement !== e.target) focoPeloMouse.current = true;
        else interagiu.current = true;
        if (onMouseDown) onMouseDown(e);
    };
    const tratarMouseUp = (e) => {
        if (focoPeloMouse.current) {
            focoPeloMouse.current = false;
            if (selecionarAoFocar && !bloqueado && !alterado.current && !interagiu.current) {
                e.preventDefault();
                selecionarTudo();
            }
        }
        if (onMouseUp) onMouseUp(e);
    };

    return (
        <input
            inputMode="decimal"
            autoComplete="off"
            {...props}
            type="text"
            ref={inputRef}
            value={editando ? texto : exibido}
            onChange={tratarMudanca}
            onPaste={tratarColagem}
            onFocus={tratarFoco}
            onBlur={tratarBlur}
            onKeyDown={tratarTecla}
            onMouseDown={tratarMouseDown}
            onMouseUp={tratarMouseUp}
        />
    );
}

/**
 * Valor monetário. `moeda` = id do orçamento (real/dolar/euro/iene/libra/dolarcad), ISO (BRL/USD/EUR)
 * ou símbolo; vira o prefixo ("R$ ", "US$ ", "€ "). Sem `moeda`, sem prefixo
 * (campo cujo rótulo já diz a moeda, ou célula estreita).
 * `milhar={false}` para número sem milhar que não é percentual (câmbio).
 */
export default function CampoValor({ moeda = null, ...props }) {
    const simbolo = simboloMoeda(moeda);
    return <CampoNumeroBase prefix={simbolo ? `${simbolo} ` : ''} {...props} />;
}

export { CampoValor };
